import { CalendarOutlined, InfoCircleOutlined, LockOutlined } from '@ant-design/icons';
import { Card, Tag, Tooltip, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowSummary } from '@/api/workflows';
import { type DisplayStatus, statusColor } from '@/originator/statusUtils';
import { assigneeOf } from '@/utils/workflowHelpers';

import { VersionChip } from './VersionChip';

const { Text, Paragraph } = Typography;

type Props = {
  /** Summary, not the full template — the card renders metadata only, so it works
   *  off the slim list payload as well as a fully-hydrated template. */
  workflow: WorkflowSummary;
  /** Label shown in the version chip (e.g. "v1.2"). */
  versionLabel: string;
  /** Status shown in the right-of-appName status tag. Accepts the BE statuses
   *  plus the derived PendingPublishing / Published states the Originator dashboard
   *  splits Approved into. */
  status: DisplayStatus;
  /** Action row contents. Consumer renders whichever icon buttons / toggles apply. */
  actions: ReactNode;
  /** Optional click handler on the status tag — used by the Originator's Rejected flow. */
  onStatusClick?: () => void;
  /** Style tweak from outside (e.g. a drag-handle wrapper sets cursor). */
  style?: React.CSSProperties;
};

/**
 * Shared workflow card body. Owns the header + sub-row + divider + assignee +
 * description layout; the action row is a slot so the Originator dashboard and
 * the Approver dashboard can pass their own action sets without forking the
 * card design.
 */
export function WorkflowCard({
  workflow: w,
  versionLabel,
  status,
  actions,
  onStatusClick,
  style,
}: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const name = (isFr ? w.nameFr : w.nameEn) || w.nameEn;
  const description = isFr ? w.descriptionFr : w.descriptionEn;
  const lockedByOther = !!w.lock && !w.lock.heldByCaller;
  // Request type — "New Form" for the workflow's very first submission cycle
  // (major version 1), "Change" for every subsequent version. Parsed off the
  // label shown on THIS card so a single workflow's Published (v1) card reads
  // as "New Form" while its in-flight v2 card correctly reads as "Change".
  const requestType = deriveRequestType(versionLabel);
  // Submitted date — only shown when the template has ever been submitted.
  // Localized to a compact date string; the exact ISO stays in the tooltip so
  // reviewers can hover to see the precise timestamp.
  const submittedDate = w.submittedAt ? new Date(w.submittedAt) : null;
  const submittedShort = submittedDate?.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const submittedFull = submittedDate?.toLocaleString(i18n.language);

  return (
    <Card
      hoverable
      style={{ borderRadius: 12, ...style }}
      styles={{ body: { padding: 18, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header: title + lock chip + version chip */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <Text strong style={{ fontSize: 16, lineHeight: 1.35, fontStyle: 'normal' }}>
          {name || w.appName}
        </Text>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {/* Lock chip — only when SOMEONE ELSE currently holds it. BE drops expired
              locks from the response so any non-null `lock` here is a live hold. */}
          {lockedByOther && w.lock && (
            <Tooltip
              title={t('workflow.lock.bannerExpiry', {
                time: new Date(w.lock.expiresAt).toLocaleString(i18n.language),
              })}
            >
              <Tag
                icon={<LockOutlined />}
                color="orange"
                style={{ marginInlineEnd: 0, fontWeight: 500 }}
              >
                {t('workflow.lock.cardChip', {
                  name:
                    (w.lock.holderName ?? null)?.split(' ')[0] ?? t('workflow.lock.anotherUser'),
                })}
              </Tag>
            </Tooltip>
          )}
          <VersionChip label={versionLabel} />
        </div>
      </div>

      {/* AppName row — pill on its own line, full width. */}
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flex: 1,
            minWidth: 0,
            background: '#f3f6f9',
            border: '1px solid #e3e8ee',
            borderRadius: 8,
            padding: '4px 12px',
            fontSize: 13,
            fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
            color: '#3a4250',
          }}
        >
          <span
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {w.appName}
          </span>
        </span>
      </div>

      {/* Badges row — Request Type + (optional) clickable Status tag. Status
          is redundant on the dashboards today because each tab already scopes
          to a single status (Drafts / Waiting for approval / Approved /
          Rejected / Published), so we only render it when the caller wires
          `onStatusClick` — the Rejected flow uses that hook to pop the
          rejection-reason modal, so the tag stays as its click target. When
          no click handler is wired, the request-type tag stretches full
          width and status disappears from the card. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 8,
          marginTop: 8,
        }}
      >
        {/* Request type — New Form / Change. See deriveRequestType — the
            badge is derived from THIS card's version label so a v1 Published
            card reads as "New Form" while the same workflow's v2 in-flight
            card reads as "Change". Fills the row when status is hidden;
            half when the status click-target is shown. */}
        <Tag
          color={requestType === 'new' ? 'blue' : 'gold'}
          style={{
            marginInlineEnd: 0,
            flex: 1,
            minWidth: 0,
            textAlign: 'center',
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          {requestType === 'new'
            ? t('workflow.list.requestTypeNewForm', { defaultValue: 'New Form' })
            : t('workflow.list.requestTypeChange', { defaultValue: 'Change' })}
        </Tag>
        {onStatusClick && (
          // Clickable status — used by the Rejected card to pop the
          // rejection-reason modal. Underline + info icon signal that the
          // tag opens more info; tooltip names the action for keyboard /
          // screen-reader users.
          <Tooltip
            title={t('workflow.list.viewRejectionReason', {
              defaultValue: 'View rejection reason',
            })}
          >
            <Tag
              color={statusColor(status)}
              className="workflow-card__status-tag--clickable"
              style={{
                marginInlineEnd: 0,
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.2,
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                textUnderlineOffset: 3,
              }}
              onClick={onStatusClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStatusClick();
                }
              }}
            >
              {t(`workflow.statusLabels.${status}`)}
              <InfoCircleOutlined style={{ fontSize: 11 }} />
            </Tag>
          </Tooltip>
        )}
      </div>

      <div style={{ height: 1, background: '#f0f0f0', margin: '14px 0' }} />

      {/* People block — Assignee (advisor target) + Created by (originator
          snapshot). Compact two-line stack; `Created by` only renders when
          we actually know the value so legacy rows without ownerName don't
          show an ugly oid fallback line unless we HAVE to. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Text style={{ fontSize: 14, fontStyle: 'normal', color: '#1f2937' }}>
          <Text strong style={{ fontStyle: 'normal', color: '#111827', fontSize: 14 }}>
            {t('workflow.list.assignee')}:
          </Text>{' '}
          {assigneeOf(w, t)}
        </Text>
        {(w.ownerName?.trim() || w.ownerOid) && (
          <Text style={{ fontSize: 14, fontStyle: 'normal', color: '#1f2937' }}>
            <Text strong style={{ fontStyle: 'normal', color: '#111827', fontSize: 14 }}>
              {t('workflow.list.createdBy', { defaultValue: 'Created by' })}:
            </Text>{' '}
            {w.ownerName?.trim() || w.ownerOid}
          </Text>
        )}
      </div>

      <Paragraph
        ellipsis={{ rows: 2 }}
        style={{
          fontSize: 13,
          marginTop: 8,
          marginBottom: 0,
          minHeight: 36,
          fontStyle: 'normal',
          color: '#4b5563',
        }}
      >
        {description || '—'}
      </Paragraph>

      {/* Submitted date — subtle inline hint tucked under the description
          so it doesn't compete with the tags on the identifier row. Muted
          text + calendar icon; full timestamp lives in the tooltip. Hidden
          entirely on Drafts that have never been submitted. */}
      {submittedShort && (
        <Tooltip title={submittedFull}>
          <div
            style={{
              marginTop: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            <CalendarOutlined style={{ fontSize: 11 }} />
            <span>
              {t('workflow.list.submittedOnShort', {
                date: submittedShort,
                defaultValue: 'Submitted {{date}}',
              })}
            </span>
          </div>
        </Tooltip>
      )}

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 14,
          paddingTop: 14,
          borderTop: '1px solid #f0f0f0',
          alignItems: 'center',
        }}
      >
        {actions}
      </div>
    </Card>
  );
}

/**
 * Read the workflow's request type from the version label shown on this card.
 * Major version 1 (labels `v1` or `v1.<n>`) is the workflow's very first
 * submission cycle — the originator is authoring / awaiting approval on a
 * brand-new smart form. Any higher major is a change to an already-approved
 * form.
 *
 * Empty / unparseable labels default to `new` — the safest fallback for a
 * fresh draft that hasn't been labelled yet. Parsing the label rather than the
 * template's `currentVersionNumber` matters because a single workflow's
 * Published card and its in-flight v2 card resolve differently: the published
 * v1 reads as New Form even while a v2 change is being drafted.
 */
function deriveRequestType(versionLabel: string): 'new' | 'change' {
  const match = /^v(\d+)/i.exec(versionLabel.trim());
  if (!match) return 'new';
  return Number(match[1]) <= 1 ? 'new' : 'change';
}
